import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  Input,
} from '@angular/core';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  DateRangeFormComponent,
  LoaderButtonComponent,
  NgCommonModule,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import { BillingMeter } from '@principle-theorem/principle-core';
import {
  type ISMSUsageRequest,
  type ISMSUsageResponse,
  type ITwilioBillingSummary,
  type ITwilioIntegrationData,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  httpsCallable,
  MomentRange,
  Region,
  serialise,
  snapshot,
  toISODate,
  type ITimePeriod,
} from '@principle-theorem/shared';
import { BehaviorSubject, ReplaySubject, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StripeSMSPricingSelectorComponent } from '../../stripe-sms-pricing-selector/stripe-sms-pricing-selector.component';

@Component({
  selector: 'pr-twilio-billing-summary',
  templateUrl: './twilio-billing-summary.component.html',
  styleUrls: ['./twilio-billing-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    DateRangeFormComponent,
    LoaderButtonComponent,
    CurrencyPipe,
    LoaderButtonComponent,
    StripeSMSPricingSelectorComponent,
  ],
})
export class TwilioBillingSummaryComponent {
  trackByBillingDetails = TrackByFunctions.index<ITwilioBillingSummary>();
  dateRange$ = new BehaviorSubject<ITimePeriod>(MomentRange.init());
  billingDetails$ = new ReplaySubject<ITwilioBillingSummary[]>(1);
  loading$ = new BehaviorSubject<boolean>(false);
  pricing$: Observable<
    { inbound: number; outbound: number; marketing: number } | undefined
  >;
  data$ = new ReplaySubject<ITwilioIntegrationData>(1);
  region = input.required<Region>();

  @Input()
  set data(data: ITwilioIntegrationData) {
    if (data) {
      this.data$.next(data);
    }
  }

  constructor(private _organisation: OrganisationService) {
    this.pricing$ = this.data$.pipe(
      map((data) => {
        const inbound = data.inboundPriceId
          ? BillingMeter.resolveCostByLookupId(data.inboundPriceId)
          : 0;
        const outbound = data.outboundPriceId
          ? BillingMeter.resolveCostByLookupId(data.outboundPriceId)
          : 0;
        const marketing = data.marketingPriceId
          ? BillingMeter.resolveCostByLookupId(data.marketingPriceId)
          : 0;

        return {
          inbound,
          outbound,
          marketing,
        };
      })
    );
  }

  updateDates(range: ITimePeriod): void {
    this.dateRange$.next({
      from: range.from,
      to: range.to,
    });
  }

  async getSMSBillingDetails(): Promise<void> {
    this.loading$.next(true);
    const dateRange = await snapshot(this.dateRange$);
    const requestData = serialise({
      from: toISODate(dateRange.from),
      to: toISODate(dateRange.to),
      orgUid: await snapshot(
        this._organisation.organisation$.pipe(
          filterUndefined(),
          map((organisation) => organisation.ref.id)
        )
      ),
    });
    const data = await snapshot(
      httpsCallable<ISMSUsageRequest, ISMSUsageResponse>(
        'http-twilio-getUsageDetails'
      )(requestData)
    );

    this.billingDetails$.next(data.practices);
    this.loading$.next(false);
  }
}
